/**
 * RenderingBookViewer — full-screen "flip through the renderings" book for a Rendering Package.
 *
 * Each rendering is a landscape (11×17) page in a react-pageflip book; turn with the ‹ › buttons or
 * the arrow keys, click a page to zoom. Portaled to <body> so it escapes the journey carousel's
 * transformed/blurred ancestors, and it captures arrow/Escape keys so the journey beneath never
 * advances while it's open (same hardening as DrawingSetViewer).
 *
 * Props: images [{ url, name? }], title, onClose()
 */
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import HTMLFlipBook from 'react-pageflip'
import { resizedImageUrl } from '../../lib/imageUrl.js'
import { getCachedPinProof } from '../../services/journey.js'

const PAGE_AR = 17 / 11   // 11×17 landscape

// On-screen sizes (Supabase serves these resized; the full-res original is kept for the PDF). If the
// transform 403s for any reason, the <img> onError falls back to the original url.
const PAGE_W_PX = 1600    // a page renders ~11×17 on screen; 1600px is crisp and ~5× lighter
const ZOOM_W_PX = 2400    // full-screen zoom wants more detail but still far lighter than the original
function onImgError(e, fullUrl) {
  if (e.currentTarget.dataset.fb) return        // already fell back — avoid a loop
  e.currentTarget.dataset.fb = '1'
  e.currentTarget.src = fullUrl
}

export default function RenderingBookViewer({ images, title, projectName, token, onClose }) {
  const [dims, setDims] = useState({ w: 520, h: 336 })
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 640)
  const [zoom, setZoom] = useState(null)   // zoomed image url, or null
  const [dl, setDl] = useState(false)
  const bookRef = useRef(null)
  const flipPrev = () => bookRef.current?.pageFlip?.()?.flipPrev?.()
  const flipNext = () => bookRef.current?.pageFlip?.()?.flipNext?.()

  async function downloadPdf() {
    if (dl) return
    setDl(true)
    try {
      const res = await fetch('/api/render-package-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrls: images.map(i => i.url), milestoneTitle: title || 'Rendering Package', projectName: projectName || '', journeyToken: token, pinProof: getCachedPinProof(token) }),
      })
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Download failed') }
      const url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a'); a.href = url; a.download = (title || 'Rendering Package') + '.pdf'; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 10000)
    } catch (e) { alert(e.message || 'Could not generate PDF.') }
    finally { setDl(false) }
  }

  useEffect(() => {
    function fit() {
      const mobile = window.innerWidth <= 640
      setIsMobile(mobile)
      const availH = window.innerHeight - (mobile ? 140 : 150)
      const availW = window.innerWidth - (mobile ? 20 : 150)
      let w, h
      if (mobile) {
        // single full-width landscape page (no facing page, no side arrows)
        w = availW; h = w / PAGE_AR
        if (h > availH) { h = availH; w = h * PAGE_AR }
      } else {
        w = Math.min(availW / 2, availH * PAGE_AR); h = w / PAGE_AR
        if (h > availH) { h = availH; w = h * PAGE_AR }
      }
      setDims({ w: Math.max(240, Math.round(w)), h: Math.max(150, Math.round(h)) })
    }
    fit()
    window.addEventListener('resize', fit)
    window.addEventListener('orientationchange', fit)
    return () => { window.removeEventListener('resize', fit); window.removeEventListener('orientationchange', fit) }
  }, [])

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') { e.stopImmediatePropagation(); if (zoom) setZoom(null); else onClose(); return }
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.stopImmediatePropagation(); e.preventDefault()
        if (zoom) return
        if (e.key === 'ArrowRight') flipNext()
        else flipPrev()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose, zoom])

  // Desktop: interleave [blank-left, image-right] so every spread reads as a blank facing page + the
  // rendering on a white 11×17 page (a soft page to curl against). Mobile: ONE page per rendering
  // (no facing page, no center-fold) so each fills the phone width instead of two tiny pages.
  const pages = []
  images.forEach((img, i) => {
    if (!isMobile) {
      const plate = (img.name ? String(img.name).replace(/\.[^./\\]+$/, '') : '') || ('Rendering ' + (i + 1))
      pages.push(
        <div key={'b' + i} style={St.page}>
          <div style={St.face}><div style={St.plateLabel}>{plate}</div></div>
          <div style={St.spineR} />
        </div>
      )
    }
    pages.push(
      <div key={'i' + i} style={St.page}>
        <div style={St.face}>
          <img
            src={resizedImageUrl(img.url, { width: PAGE_W_PX, quality: 75 })}
            onError={e => onImgError(e, img.url)}
            alt={img.name || ''} draggable={false} loading="lazy"
            onClick={() => setZoom(img.url)} style={St.img}
          />
        </div>
        {!isMobile && <div style={St.spineL} />}
      </div>
    )
  })

  return createPortal(
    <div style={St.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <style>{`.stf__item, .stf__block { background:#fff; }`}</style>

      <div style={St.header}>
        <div style={St.hTitle}>{title || 'Renderings'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <button onClick={downloadPdf} disabled={dl} style={St.hLink}>{dl ? 'Preparing PDF…' : 'Download PDF'}</button>
          <button onClick={onClose} style={St.hClose} title="Close (Esc)">✕</button>
        </div>
      </div>

      <div style={St.stage}>
        {!isMobile && <button style={St.nav} onClick={flipPrev} title="Previous (←)">‹</button>}
        <HTMLFlipBook
          key={(isMobile ? 'm' : 'd') + ':' + images.length}
          ref={bookRef}
          width={dims.w}
          height={dims.h}
          size="fixed"
          usePortrait={isMobile}
          showCover={false}
          useMouseEvents={true}
          showPageCorners={!isMobile}
          mobileScrollSupport
          drawShadow={!isMobile}
          maxShadowOpacity={0.5}
          flippingTime={600}
        >
          {pages}
        </HTMLFlipBook>
        {!isMobile && <button style={St.nav} onClick={flipNext} title="Next (→)">›</button>}
      </div>

      {isMobile && (
        <div style={St.mnav}>
          <button style={St.mbtn} onClick={flipPrev}>‹ Prev</button>
          <button style={St.mbtn} onClick={flipNext}>Next ›</button>
        </div>
      )}

      <div style={St.hint}>
        {isMobile ? 'Tap a rendering to zoom · swipe or use Prev / Next' : 'Click a rendering to zoom · turn at the ‹ › buttons or with the arrow keys'}
      </div>

      {zoom && (
        <div style={St.zoomWrap} onClick={() => setZoom(null)}>
          <img src={resizedImageUrl(zoom, { width: ZOOM_W_PX, quality: 82 })} onError={e => onImgError(e, zoom)} alt="" style={St.zoomImg} />
          <div style={St.zoomHint}>Click anywhere to return</div>
        </div>
      )}
    </div>,
    document.body
  )
}

const St = {
  overlay: { position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(7,39,45,0.94)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 16px 10px' },
  header: { width: '100%', maxWidth: 1400, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, color: '#fff', marginBottom: 8 },
  hTitle: { fontFamily: '"DM Sans",sans-serif', fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  hClose: { background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: 6, width: 30, height: 30, fontSize: 15, cursor: 'pointer', lineHeight: 1 },
  hLink: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, cursor: 'pointer', padding: 4, whiteSpace: 'nowrap' },
  stage: { flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 },
  nav: { background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: 8, width: 40, height: 64, fontSize: 26, cursor: 'pointer', flexShrink: 0, lineHeight: 1 },
  // Mobile: page-turn controls sit in a bottom bar (full-width tap targets) instead of side arrows.
  mnav: { display: 'flex', gap: 10, width: '100%', maxWidth: 460, marginTop: 8 },
  mbtn: { flex: 1, background: 'rgba(255,255,255,0.14)', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontSize: 14, fontWeight: 600, fontFamily: '"DM Sans",sans-serif', cursor: 'pointer' },
  // White 11×17 page; the image is anchored centered with margins, a blank page faces it.
  page: { width: '100%', height: '100%', background: '#fff', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10, boxSizing: 'border-box' },
  face: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', backfaceVisibility: 'hidden' },
  // Subtle drawing title on the facing (left) page — a quiet plate caption.
  plateLabel: { fontFamily: '"Montserrat",sans-serif', fontSize: 11, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#9aaeb4', padding: '0 12%', textAlign: 'center' },
  img: { maxWidth: '86%', maxHeight: '86%', objectFit: 'contain', cursor: 'zoom-in', boxShadow: '0 2px 16px rgba(7,39,45,0.13)' },
  // Center-fold shadows (inner edge of each page), matching the drawing/schedule viewers.
  spineR: { position: 'absolute', top: 0, right: 0, bottom: 0, width: '12%', pointerEvents: 'none', borderRight: '1px solid rgba(7,39,45,0.16)', background: 'linear-gradient(to right, rgba(7,39,45,0) 0%, rgba(7,39,45,0.04) 58%, rgba(7,39,45,0.15) 100%)' },
  spineL: { position: 'absolute', top: 0, left: 0, bottom: 0, width: '12%', pointerEvents: 'none', borderLeft: '1px solid rgba(7,39,45,0.16)', background: 'linear-gradient(to left, rgba(7,39,45,0) 0%, rgba(7,39,45,0.04) 58%, rgba(7,39,45,0.15) 100%)' },
  hint: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'rgba(255,255,255,0.55)', marginTop: 8, textAlign: 'center' },
  zoomWrap: { position: 'fixed', inset: 0, zIndex: 9700, background: 'rgba(7,39,45,0.96)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' },
  zoomImg: { maxWidth: '96vw', maxHeight: '92vh', objectFit: 'contain' },
  zoomHint: { position: 'fixed', bottom: 16, left: 0, right: 0, textAlign: 'center', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.6)', pointerEvents: 'none' },
}
